import React, { useEffect, useState } from "react";
import {
  addCategory,
  updateCategory,
  getCategories,
  deleteCategory,
} from "../../services/categoryService";

import { toast } from "react-toastify";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

import "./CategoryLayer.css";

const CategoryLayer = () => {
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    name: "",
    description: "",
  });

  /* =========================
     FETCH DATA
  ========================= */
  const fetchCategories = async () => {
    const res = await getCategories();

    if (res.status === "success") {
      setCategories(res.data || []);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  /* =========================
     INPUT CHANGE
  ========================= */
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  /* =========================
     ADD / UPDATE
  ========================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    let res;

    if (editingId) {
      res = await updateCategory(editingId, form);
    } else {
      res = await addCategory(form);
    }

    if (res.status === "success") {
      toast.success(res.message);
      fetchCategories();
    } else {
      toast.error(res.message);
    }

    setOpen(false);
    setEditingId(null);
    setForm({ name: "", description: "" });
  };

  /* =========================
     EDIT
  ========================= */
  const handleEdit = (cat) => {
    setEditingId(cat.id);

    setForm({
      name: cat.name,
      description: cat.description,
    });

    setOpen(true);
  };

  /* =========================
     DELETE
  ========================= */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this category?")) return;

    const res = await deleteCategory(id);

    if (res.status === "success") {
      toast.success("Category deleted");
      fetchCategories();
    } else {
      toast.error("Delete failed");
    }
  };

  /* =========================
     SEARCH FILTER
  ========================= */
  const filtered = categories.filter((c) =>
    `${c.name} ${c.description}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  /* =========================
     PAGINATION
  ========================= */
  const totalPages = Math.ceil(filtered.length / pageSize);

  const displayed = filtered.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  return (
    <div className="category-wrapper">
      <div className="category-card">

        {/* HEADER */}
        <div className="list-header">
          <h5>Category Management</h5>

          <button
            className="btn-primary"
            onClick={() => {
              setOpen(true);
              setEditingId(null);
              setForm({ name: "", description: "" });
            }}
          >
            <FaPlus /> Add Category
          </button>
        </div>

        {/* CONTROLS */}
        <div className="list-controls">

          {/* SEARCH */}
          <div className="search-box">
            <FaSearch />
            <input
              type="text"
              placeholder="Search category..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>

          {/* PAGE SIZE */}
          <div className="page-size">
            <label>Show</label>

            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>

            <span>entries</span>
          </div>

        </div>

        {/* TABLE */}
        <div className="table-responsive">
          <table className="user-table">

            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Description</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>

            <tbody>

              {displayed.length === 0 ? (
                <tr>
                  <td colSpan="4" className="no-data">
                    No categories found
                  </td>
                </tr>
              ) : (
                displayed.map((cat, i) => (
                  <tr key={cat.id}>
                    <td>{(page - 1) * pageSize + i + 1}</td>
                    <td>{cat.name}</td>
                    <td>{cat.description}</td>

                    <td className="actions">

                      <button onClick={() => handleEdit(cat)}>
                        <FaEdit />
                      </button>

                      <button onClick={() => handleDelete(cat.id)}>
                        <FaTrash />
                      </button>

                    </td>
                  </tr>
                ))
              )}

            </tbody>

          </table>
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="pagination">

            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <FaChevronLeft />
            </button>

            <span>
              Page {page} of {totalPages}
            </span>

            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              <FaChevronRight />
            </button>

          </div>
        )}

      </div>

      {/* MODAL */}
      {open && (
        <div className="modal-overlay">

          <div className="modal-box">

            <h4>{editingId ? "Update Category" : "Add Category"}</h4>

            <form onSubmit={handleSubmit}>

              <div className="form-group">
                <label>Category Name</label>

                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Enter category name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>

                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Enter description"
                />
              </div>

              <div className="modal-actions">

                <button type="submit" className="btn-primary">
                  {editingId ? "Update" : "Add"}
                </button>

                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </button>

              </div>

            </form>

          </div>

        </div>
      )}
    </div>
  );
};

export default CategoryLayer;